/**
 * ChainVault WP - Shared Send/Transfer Logic
 *
 * Single source of truth for sending ANY asset (native coin, ERC20 token,
 * or Solana SPL token). Used by both the per-row send modal in AssetList.js
 * and the generalized send modal in WalletPage.js, so a fix here fixes
 * both places at once instead of drifting out of sync.
 *
 * @package ChainVault
 * @since   1.0.0
 */

/**
 * Send an asset to a recipient.
 *
 * @param {object}   params
 * @param {object}   params.asset          The asset being sent (symbol, network, address/mint, decimals, id, type)
 * @param {string}   params.sendTo         Recipient address
 * @param {string}   params.sendAmount     Amount to send (string or number)
 * @param {string}   params.walletType     'builtin' | 'metamask'
 * @param {object}   params.builtInWallet  Built-in wallet object (private keys)
 * @param {Function} params.sendFromBuiltIn Context helper for native transfers
 * @return {Promise<{hash: string|null}>}
 */
export async function sendAsset( { asset, sendTo, sendAmount, walletType, builtInWallet, sendFromBuiltIn } ) {
    if ( ! sendTo ) {
        throw new Error( 'Please enter a recipient address.' );
    }
    if ( ! sendAmount || parseFloat( sendAmount ) <= 0 ) {
        throw new Error( 'Please enter a valid amount.' );
    }

    const { ethers } = await import( 'ethers' );
    const isTestnet  = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';

    /* Check if this is a custom ERC20 token (has contract address) */
    const isCustomToken = asset.address && asset.address.startsWith( '0x' ) &&
        ! [ 'ethereum', 'bsc', 'polygon', 'base' ].includes( asset.id );

    if ( isCustomToken ) {
        /* ERC20 token transfer */
        const rpc = isTestnet ? 'https://ethereum-sepolia-rpc.publicnode.com' : 'https://ethereum-rpc.publicnode.com';
        const erc20Abi = [
            'function transfer(address to, uint256 amount) returns (bool)',
            'function decimals() view returns (uint8)',
        ];
        let signer;
        if ( walletType === 'builtin' && builtInWallet ) {
            const provider = new ethers.JsonRpcProvider( rpc );
            signer = new ethers.Wallet( builtInWallet.ethereum.privateKey, provider );
        } else if ( walletType === 'metamask' && window.ethereum ) {
            const provider = new ethers.BrowserProvider( window.ethereum );
            signer = await provider.getSigner();
        } else {
            throw new Error( 'No wallet connected.' );
        }
        const contract = new ethers.Contract( asset.address, erc20Abi, signer );
        const decimals = asset.decimals || await contract.decimals();
        const amount   = ethers.parseUnits( sendAmount.toString(), decimals );
        const tx       = await contract.transfer( sendTo, amount );
        await tx.wait();

        /* Update balance in localStorage */
        try {
            const stored  = JSON.parse( localStorage.getItem( 'chainvault_custom_tokens' ) || '[]' );
            const updated = stored.map( t => {
                if ( t.address.toLowerCase() === asset.address.toLowerCase() ) {
                    const newBal = parseFloat( t.balance ) - parseFloat( sendAmount );
                    return { ...t, balance: newBal.toString() };
                }
                return t;
            } );
            localStorage.setItem( 'chainvault_custom_tokens', JSON.stringify( updated ) );
            window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );
        } catch ( e ) {}

        return { hash: tx.hash };
    }

    if ( walletType === 'builtin' && ( asset.type === 'spl' || asset.mint ) && asset.network === 'solana' && ( asset.mint || asset.address ) ) {
        /*
         * SPL token transfer (e.g. USDT, USDC on Solana).
         * Uses spl-token transfer, NOT SystemProgram.transfer —
         * the two are completely different code paths. Mixing them
         * up causes the SOL balance check to run against the token
         * amount, producing the "need X SOL" error incorrectly.
         */
        const web3       = await import( '@solana/web3.js' );
        const splToken   = await import( '@solana/spl-token' );
        const rpc        = isTestnet ? 'https://api.devnet.solana.com' : 'https://api.mainnet-beta.solana.com';
        const connection = new web3.Connection( rpc, 'confirmed' );
        const payer      = web3.Keypair.fromSecretKey( new Uint8Array( builtInWallet.solana.secretKey ) );
        const mintPk     = new web3.PublicKey( asset.mint || asset.address );
        const toPk       = new web3.PublicKey( sendTo );
        const decimals   = asset.decimals ?? 6;
        const tokenAmount = Math.floor( parseFloat( sendAmount ) * Math.pow( 10, decimals ) );

        const fromATA = await splToken.getOrCreateAssociatedTokenAccount( connection, payer, mintPk, payer.publicKey );
        const toATA   = await splToken.getOrCreateAssociatedTokenAccount( connection, payer, mintPk, toPk );

        const sig = await splToken.transfer( connection, payer, fromATA.address, toATA.address, payer, tokenAmount );
        window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );

        return { hash: sig };
    }

    if ( walletType === 'builtin' ) {
        const txHash = await sendFromBuiltIn( sendTo, sendAmount, asset.network );
        return { hash: txHash };
    }

    if ( walletType === 'metamask' ) {
        const provider = new ethers.BrowserProvider( window.ethereum );
        const signer   = await provider.getSigner();
        const tx       = await signer.sendTransaction( {
            to:    sendTo,
            value: ethers.parseEther( sendAmount.toString() ),
        } );
        await tx.wait();
        return { hash: tx.hash };
    }

    return { hash: null };
}
